import { PageControls, usePagination } from '@/components/shared/pagination/Pagination';
import tableStrings from '@/components/shared/table/table.translations';
import { secondsToTimeString } from '@/helpers/dateHelper';
import { Contract } from '@/services/contract/contracts.types';
import { FC, FunctionComponent, ReactNode } from 'react';
import { FiEdit } from 'react-icons/fi';
import strings from '../admin.translations';
import { COLUMN_DEFINITIONS, ColumnKey } from './AdminContractTable.config';

const pageSize = 20;

const getCellContent = (contract: Contract, key: ColumnKey): ReactNode => {
  switch (key) {
    case 'name':
      return contract.name ?? '—';
    case 'contractCode':
      return contract.contractCode ?? '—';
    case 'displayOrder':
      return contract.displayOrder;
    case 'maxHoursPerWeek':
      return contract.maxHoursPerWeek;
    case 'maxHoursPerDay':
      return contract.maxHoursPerDay ?? '—';
    case 'maxHoursPerShift':
      return contract.maxHoursPerShift;
    case 'earliestStartTime':
      return secondsToTimeString(contract.earliestStartTime ?? 0);
    case 'latestEndTime':
      return secondsToTimeString(contract.latestEndTime ?? 0);
    default:
      return '—';
  }
};

interface ContractTableHeaderProps {
  visibleColumns: ColumnKey[];
}

const ContractTableHeader: FC<ContractTableHeaderProps> = ({ visibleColumns }) => (
  <thead>
    <tr>
      {visibleColumns.map(key => {
        const col = COLUMN_DEFINITIONS.find(c => c.key === key)!;
        return (
          <th
            key={key}
            className={`whitespace-nowrap px-6 pb-2 pt-4 text-xs font-medium tracking-wider text-secondary-std ${
              col.align === 'center' ? 'text-center' : 'text-left'
            }`}
          >
            {strings.contractsSection.table[col.key]}
          </th>
        );
      })}
      <th className="whitespace-nowrap px-6 pb-2 pt-4 text-center text-xs font-medium tracking-wider text-secondary-std">
        {strings.contractsSection.table.edit}
      </th>
    </tr>
  </thead>
);

interface ContractTableRowProps {
  contract: Contract;
  index: number;
  visibleColumns: ColumnKey[];
  onEdit: (id: string) => void;
}

const AdminContractTableRow: FC<ContractTableRowProps> = ({ contract, index, visibleColumns, onEdit }) => {
  const rowClass = !contract.active ? 'bg-gray-300' : index % 2 === 0 ? 'bg-secondary-std' : 'bg-primary-std';

  return (
    <tr className={rowClass}>
      {visibleColumns.map(key => {
        const col = COLUMN_DEFINITIONS.find(c => c.key === key)!;
        return (
          <td
            key={key}
            className={`whitespace-nowrap px-4 py-4 text-sm font-medium text-primary-std sm:px-6 ${
              col.align === 'center' ? 'text-center' : 'text-left'
            }`}
          >
            {key === 'name' ? (
              <div className="flex h-[2.2rem] flex-col justify-center">
                <p className="font-semibold">{contract.name ?? '—'}</p>
                <p className="text-xs text-secondary-std">
                  {contract.active ? strings.contractsSection.table.active : strings.contractsSection.table.disabled}
                </p>
              </div>
            ) : (
              getCellContent(contract, key)
            )}
          </td>
        );
      })}
      <td className="whitespace-nowrap px-4 py-4 text-center text-sm text-primary-std sm:px-6">
        <button
          type="button"
          aria-label={`${strings.contractsSection.table.edit} ${contract.name}`}
          className="text-secondary-std hover:text-primary-std"
          onClick={() => onEdit(contract.id)}
        >
          <FiEdit className="h-5 w-5" />
        </button>
      </td>
    </tr>
  );
};

type AdminContractTableProps = {
  contracts: Contract[];
  visibleColumns: ColumnKey[];
  onEdit: (id: string) => void;
};

const AdminContractTable: FunctionComponent<AdminContractTableProps> = ({ contracts, visibleColumns, onEdit }) => {
  const [offset, limit, controls] = usePagination(pageSize, contracts.length);
  const paginatedContracts = contracts.slice(offset, offset + limit);

  return (
    <div className="w-full">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-primary-std border border-primary-std">
          <ContractTableHeader visibleColumns={visibleColumns} />
          <tbody className="divide-y divide-primary-std">
            {paginatedContracts.map((contract, index) => (
              <AdminContractTableRow
                key={contract.id}
                contract={contract}
                index={index}
                visibleColumns={visibleColumns}
                onEdit={onEdit}
              />
            ))}
          </tbody>
        </table>
      </div>

      {contracts.length > 0 ? (
        <PageControls {...controls} />
      ) : (
        <div className="flex w-full justify-center border-t border-primary-std px-6 py-5">
          <p className="py-1 text-xs font-medium uppercase text-primary-std">{tableStrings.nothingToShow}</p>
        </div>
      )}
    </div>
  );
};

export default AdminContractTable;
